import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, btn, field } from "@/components/ui-kit";
import { As2Panel } from "@/components/As2Panel";
import { TestModePanel } from "@/components/TestModePanel";
import { AlertsPanel } from "@/components/AlertsPanel";
import { DocsPanel } from "@/components/DocsPanel";
import { SamplesPanel } from "@/components/SamplesPanel";
import { SettingsBackupPanel } from "@/components/SettingsBackupPanel";

const GROUPS = [
  ["VAN & interchange IDs", [["van_name", "VAN name"], ["ep_isa_qualifier", "EP ISA qualifier (ISA05)"], ["ep_isa_id", "EP ISA ID - production (ISA06)"], ["ep_gs_id", "EP GS ID - production (GS02)"], ["ep_isa_id_test", "EP ISA ID - test"], ["ep_gs_id_test", "EP GS ID - test"],
    ["go_isa_qualifier", "Group O ISA qualifier (ISA07)"], ["go_isa_id", "Group O ISA ID - production (ISA08)"], ["go_gs_id", "Group O GS ID - production (GS03)"], ["go_isa_id_test", "Group O ISA ID - test"], ["go_gs_id_test", "Group O GS ID - test"], ["usage_indicator", "Usage T/P (ISA15)"]]],
  ["Euro Packaging plant (850 ship-to)", [["ep_name", "Name"], ["ep_code", "Code"], ["ep_plant_address", "Address"], ["ep_plant_city", "City"], ["ep_plant_state", "State"], ["ep_plant_zip", "Postcode"], ["ep_plant_country", "Country"]]],
  ["Bill-to on 940 (N1*BT)", [["bt_name", "Name"], ["bt_address", "Address"], ["bt_city", "City"], ["bt_state", "State"], ["bt_zip", "Zip"], ["bt_country", "Country"]]],
  ["Group O warehouse party (940 N1*WH)", [["go_name", "Name"], ["go_wh_code", "WH code"], ["go_address", "Address"], ["go_city", "City"], ["go_state", "State"], ["go_zip", "Zip"]]],
  ["Stock policy (MOU)", [["min_days", "Minimum days of stock"], ["peak_days", "Peak days (1 Oct – 15 Jan)"], ["replen_lead_days", "Days until truck arrives"], ["pallets_per_truck", "Pallets per truck [placeholder]"]]],
];

const SiteRow = ({ s, isMgr, partners }) => {
  const [f, setF] = useState(s);
  const save = () => api.put(`/sites/${s.code}`, f).then(() => toast.success(`${s.code} saved`)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid items-end gap-2 border-b border-slate-800 px-5 py-3 last:border-0 sm:grid-cols-[60px_110px_1fr_1fr_1.4fr_1fr_60px_80px_auto]">
      <div className="font-mono text-sm text-emerald-300">{s.code}</div>
      <select value={f.partner || "GROUPO"} disabled={!isMgr || s.kind !== "xdock"} onChange={(e) => setF({ ...f, partner: e.target.value })} className={`h-8 rounded-md border px-1 text-xs ${field}`} data-testid={`site-${s.code}-partner`}>
        {partners.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
      </select>
      {["name", "groupo_id", "address", "city", "state", "zip"].map((k) => (
        <Input key={k} value={f[k] || ""} disabled={!isMgr} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`h-8 text-xs ${field}`} placeholder={k} data-testid={`site-${s.code}-${k}`} />
      ))}
      {isMgr && <Button size="sm" onClick={save} className={btn.ghost} variant="ghost" data-testid={`site-${s.code}-save`}>Save</Button>}
    </div>
  );
};

const AddSiteRow = ({ partners, onAdded }) => {
  const empty = { code: "", partner: "", name: "", groupo_id: "", address: "", city: "", state: "", zip: "" };
  const [f, setF] = useState(empty);
  const add = () => api.post("/sites", f).then(() => { toast.success(`Site ${f.code} added`); setF(empty); onAdded(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid items-end gap-2 bg-slate-900/40 px-5 py-3 sm:grid-cols-[60px_110px_1fr_1fr_1.4fr_1fr_60px_80px_auto]" data-testid="site-add-row">
      <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="Code" className={`h-8 font-mono text-xs ${field}`} data-testid="site-add-code" />
      <select value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} className={`h-8 rounded-md border px-1 text-xs ${field}`} data-testid="site-add-partner">
        <option value="">Partner…</option>
        {partners.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
      </select>
      {["name", "groupo_id", "address", "city", "state", "zip"].map((k) => (
        <Input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={k === "groupo_id" ? "partner site ID" : k} className={`h-8 text-xs ${field}`} data-testid={`site-add-${k}`} />
      ))}
      <Button size="sm" onClick={add} disabled={!f.code || !f.name || !f.partner} className={btn.primary} data-testid="site-add-btn">Add site</Button>
    </div>
  );
};

export default function Settings() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [sites, setSites] = useState([]);
  const [partnerList, setPartnerList] = useState([]);
  const isMgr = user.role === "manager";
  useEffect(() => {
    api.get("/settings").then((r) => setCfg(r.data));
    api.get("/sites").then((r) => setSites(r.data));
    api.get("/partners").then((r) => setPartnerList(r.data.partners));
  }, []);
  const save = async () => {
    try {
      setCfg((await api.put("/settings", { ...cfg, min_days: Number(cfg.min_days), peak_days: Number(cfg.peak_days), replen_lead_days: Number(cfg.replen_lead_days), pallets_per_truck: Number(cfg.pallets_per_truck), ack_overdue_hours: Number(cfg.ack_overdue_hours) || 24 })).data);
      window.dispatchEvent(new Event("ep-settings"));
      toast.success("Partner settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!cfg) return null;
  return (
    <div className="space-y-6">
      <PageHeader title="Partner settings" subtitle="Interchange IDs and party data used to build outbound X12. Values marked TBC are placeholders — replace with your VAN details before go-live.">
        {isMgr && <Button onClick={save} className={btn.primary} data-testid="settings-save-btn">Save settings</Button>}
      </PageHeader>
      <DocsPanel />
      {isMgr && <SettingsBackupPanel onRestored={(c) => { setCfg(c); api.get("/sites").then((r) => setSites(r.data)); window.dispatchEvent(new Event("ep-settings")); }} />}
      <SamplesPanel />
      <TestModePanel cfg={cfg} setCfg={setCfg} />
      <AlertsPanel cfg={cfg} setCfg={setCfg} />
      {GROUPS.map(([title, fields]) => (
        <Panel key={title} title={title}>
          <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            {fields.map(([k, l]) => (
              <label key={k} className="block">
                <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
                <Input value={cfg[k] ?? ""} disabled={!isMgr} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className={field} data-testid={`settings-${k}`} />
              </label>
            ))}
          </div>
        </Panel>
      ))}
      <As2Panel cfg={cfg} setCfg={setCfg} isMgr={isMgr} />
      <Panel title="Partner sites (xDock / warehouse)" testId="settings-sites-panel">
        {sites.map((s) => <SiteRow key={s.code} s={s} isMgr={isMgr} partners={partnerList} />)}
        {isMgr && <AddSiteRow partners={partnerList} onAdded={() => api.get("/sites").then((r) => setSites(r.data))} />}
      </Panel>
    </div>
  );
}
